import type { AppUser, CreateLegalEntity, LegalEntity, LegalEntityDetail, UpdateLegalEntity } from './legal-entity';
import type { LabourRateTier, UpsertLabourRateTier } from './labour-rate-tier';
import type { MilestoneTemplate, UpsertMilestoneTemplate } from './milestone-template';
import type { QuoteConfig, UpdateQuoteConfig } from './quote-config';
import type {
  RoomCalculationConfig,
  RoomGeometryMultiplier,
  RoomHeightCoefficient,
  UpdateRoomCalculationConfig,
  UpsertRoomGeometryMultiplier,
  UpsertRoomHeightCoefficient,
} from './room-calculation-config';
import type { TaskGenConfig, UpdateTaskGenConfig } from './task-gen-config';

import { api } from '@/utils/api';

export const legalEntityApi = {
  fetchAll: (includeArchived = false) => api.get(`api/v1/web/legal-entity?includeArchived=${includeArchived}`).json<LegalEntity[]>(),

  fetchMine: () => api.get('api/v1/web/legal-entity/mine').json<LegalEntity[]>(),

  get: (id: string) => api.get(`api/v1/web/legal-entity/${id}`).json<LegalEntityDetail>(),

  create: (body: CreateLegalEntity) => api.post('api/v1/web/legal-entity', { json: body }).json<LegalEntity>(),

  update: (id: string, body: UpdateLegalEntity) => api.put(`api/v1/web/legal-entity/${id}`, { json: body }).json<LegalEntity>(),

  archive: (id: string) => api.delete(`api/v1/web/legal-entity/${id}`),

  assignUser: (entityId: string, userId: string) => api.post(`api/v1/web/legal-entity/${entityId}/users`, { json: { userId } }),

  removeUser: (entityId: string, userId: string) => api.delete(`api/v1/web/legal-entity/${entityId}/users/${userId}`),

  searchUsers: (query?: string) => {
    const qs = query ? `?query=${encodeURIComponent(query)}` : '';
    return api.get(`api/v1/web/legal-entity/users/search${qs}`).json<AppUser[]>();
  },

  getQuoteConfig: (entityId: string) => api.get(`api/v1/web/legal-entity/${entityId}/quote-config`).json<QuoteConfig>(),

  updateQuoteConfig: (entityId: string, body: UpdateQuoteConfig) =>
    api.put(`api/v1/web/legal-entity/${entityId}/quote-config`, { json: body }).json<QuoteConfig>(),

  getLabourRateTiers: (entityId: string) => api.get(`api/v1/web/legal-entity/${entityId}/labour-rate-tiers`).json<LabourRateTier[]>(),

  upsertLabourRateTiers: (entityId: string, tiers: UpsertLabourRateTier[]) =>
    api.put(`api/v1/web/legal-entity/${entityId}/labour-rate-tiers`, { json: { tiers } }).json<LabourRateTier[]>(),

  getTaskGenConfig: (entityId: string) => api.get(`api/v1/web/legal-entity/${entityId}/task-gen-config`).json<TaskGenConfig>(),

  updateTaskGenConfig: (entityId: string, body: UpdateTaskGenConfig) =>
    api.put(`api/v1/web/legal-entity/${entityId}/task-gen-config`, { json: body }).json<TaskGenConfig>(),

  getRoomCalculationConfig: (entityId: string) =>
    api.get(`api/v1/web/legal-entity/${entityId}/room-calculation-config`).json<RoomCalculationConfig>(),

  updateRoomCalculationConfig: (entityId: string, body: UpdateRoomCalculationConfig) =>
    api.put(`api/v1/web/legal-entity/${entityId}/room-calculation-config`, { json: body }).json<RoomCalculationConfig>(),

  getRoomHeightCoefficients: (entityId: string) =>
    api.get(`api/v1/web/legal-entity/${entityId}/room-height-coefficients`).json<RoomHeightCoefficient[]>(),

  upsertRoomHeightCoefficients: (entityId: string, coefficients: UpsertRoomHeightCoefficient[]) =>
    api.put(`api/v1/web/legal-entity/${entityId}/room-height-coefficients`, { json: { coefficients } }).json<RoomHeightCoefficient[]>(),

  getRoomGeometryMultipliers: (entityId: string) =>
    api.get(`api/v1/web/legal-entity/${entityId}/room-geometry-multipliers`).json<RoomGeometryMultiplier[]>(),

  upsertRoomGeometryMultipliers: (entityId: string, multipliers: UpsertRoomGeometryMultiplier[]) =>
    api.put(`api/v1/web/legal-entity/${entityId}/room-geometry-multipliers`, { json: { multipliers } }).json<RoomGeometryMultiplier[]>(),

  resetRoomCalculationDefaults: (entityId: string) => api.post(`api/v1/web/legal-entity/${entityId}/room-calculation-config/reset`),

  getMilestoneTemplates: (entityId: string) =>
    api.get(`api/v1/web/legal-entity/${entityId}/milestone-templates`).json<MilestoneTemplate[]>(),

  createMilestoneTemplate: (entityId: string, body: UpsertMilestoneTemplate) =>
    api.post(`api/v1/web/legal-entity/${entityId}/milestone-templates`, { json: body }).json<MilestoneTemplate>(),

  updateMilestoneTemplate: (entityId: string, templateId: string, body: UpsertMilestoneTemplate) =>
    api.put(`api/v1/web/legal-entity/${entityId}/milestone-templates/${templateId}`, { json: body }).json<MilestoneTemplate>(),

  deleteMilestoneTemplate: (entityId: string, templateId: string) =>
    api.delete(`api/v1/web/legal-entity/${entityId}/milestone-templates/${templateId}`),

  reorderMilestoneTemplates: (entityId: string, items: { id: string; sortOrder: number }[]) =>
    api.post(`api/v1/web/legal-entity/${entityId}/milestone-templates/reorder`, { json: { items } }).then(() => {}),
};
